/**
 * Column Mapping Modal Component
 * Allows users to select which CSV column contains product descriptions
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    TableChart as TableIcon,
} from '@mui/icons-material';

interface FilePreviewData {
    transaction_id: string;
    file_name: string;
    file_type: string;
    columns: string[];
    preview_rows: Record<string, string | null>[];
    suggested_description_col: string | null;
    suggested_hs_code_col: string | null;
    total_rows: number;
    status: string;
}

interface ColumnMappingModalProps {
    open: boolean;
    onClose: () => void;
    previewData: FilePreviewData | null;
    onConfirm: (descriptionColumn: string, hsCodeColumn?: string) => Promise<void>;
    loading?: boolean;
}

const ColumnMappingModal: React.FC<ColumnMappingModalProps> = ({
    open,
    onClose,
    previewData,
    onConfirm,
    loading = false,
}) => {
    const [descriptionColumn, setDescriptionColumn] = useState<string>('');
    const [hsCodeColumn, setHsCodeColumn] = useState<string>('');
    const [confirming, setConfirming] = useState(false);

    // Set suggested columns when preview data loads
    useEffect(() => {
        if (previewData) {
            if (previewData.suggested_description_col) {
                setDescriptionColumn(previewData.suggested_description_col);
            } else if (previewData.columns.length > 0) {
                setDescriptionColumn(previewData.columns[0]);
            }
            if (previewData.suggested_hs_code_col) {
                setHsCodeColumn(previewData.suggested_hs_code_col);
            }
        }
    }, [previewData]);

    const handleConfirm = async () => {
        if (!descriptionColumn) return;

        setConfirming(true);
        try {
            await onConfirm(descriptionColumn, hsCodeColumn || undefined);
        } finally {
            setConfirming(false);
        }
    };

    if (!previewData) return null;

    // Get sample values from selected column
    const getSampleValues = (column: string): string[] => {
        return previewData.preview_rows
            .map(row => row[column])
            .filter((val): val is string => val !== null && val !== '')
            .slice(0, 3);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableIcon color="primary" />
                Map Columns - {previewData.file_name}
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Select which column contains the <strong>product descriptions</strong> to classify.
                    Found <strong>{previewData.total_rows}</strong> rows in your file.
                </Alert>

                {/* Column Selection */}
                <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                    <FormControl fullWidth required>
                        <InputLabel>Description Column *</InputLabel>
                        <Select
                            value={descriptionColumn}
                            onChange={(e) => setDescriptionColumn(e.target.value)}
                            label="Description Column *"
                        >
                            {previewData.columns.map((col) => (
                                <MenuItem key={col} value={col}>
                                    {col}
                                    {col === previewData.suggested_description_col && (
                                        <Chip label="Suggested" size="small" color="primary" sx={{ ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>HS Code Column (Optional)</InputLabel>
                        <Select
                            value={hsCodeColumn}
                            onChange={(e) => setHsCodeColumn(e.target.value)}
                            label="HS Code Column (Optional)"
                        >
                            <MenuItem value="">None</MenuItem>
                            {previewData.columns.map((col) => (
                                <MenuItem key={col} value={col}>
                                    {col}
                                    {col === previewData.suggested_hs_code_col && (
                                        <Chip label="Suggested" size="small" color="secondary" sx={{ ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Sample Values Preview */}
                {descriptionColumn && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Sample values from "{descriptionColumn}":
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {getSampleValues(descriptionColumn).map((val, idx) => (
                                <Chip
                                    key={idx}
                                    label={val.length > 50 ? val.substring(0, 50) + '...' : val}
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Data Preview Table */}
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Data Preview (First 5 Rows)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {previewData.columns.map((col) => (
                                    <TableCell
                                        key={col}
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor: col === descriptionColumn ? 'primary.light' :
                                                col === hsCodeColumn ? 'secondary.light' : 'grey.100',
                                            color: col === descriptionColumn || col === hsCodeColumn ? 'white' : 'inherit',
                                        }}
                                    >
                                        {col}
                                        {col === descriptionColumn && <CheckIcon sx={{ ml: 0.5, fontSize: 16 }} />}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewData.preview_rows.map((row, rowIdx) => (
                                <TableRow key={rowIdx}>
                                    {previewData.columns.map((col) => (
                                        <TableCell
                                            key={col}
                                            sx={{
                                                bgcolor: col === descriptionColumn ? 'primary.50' : 'inherit',
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row[col] || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={confirming}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!descriptionColumn || confirming || loading}
                    startIcon={confirming ? <CircularProgress size={20} /> : <CheckIcon />}
                >
                    {confirming ? 'Processing...' : 'Confirm & Classify'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ColumnMappingModal;
